/**
 * Hierarchical react-query key factory for the customer portal (SIL-320).
 *
 * Replaces 63 distinct hand-written key literals (previously scattered
 * across route/component files, several missing the `client-` prefix
 * convention, one silently stale — see the `assets.detail` /
 * `assets.listAll` split below) with a single source of truth. Every
 * `useQuery`/`invalidateQueries` call site in apps/customer imports from
 * here — grep for a raw `queryKey: ['` literal outside this file is a bug.
 *
 * Convention: every top-level key starts with `'client-'` (this app talks to
 * exactly one API surface, `/api/client/*`, so the prefix disambiguates
 * nothing today, but it matches the pre-existing majority convention and
 * keeps devtools output greppable). `list(params)` keys include the params
 * object/tuple as a trailing key segment so distinct filter combinations
 * cache independently; `detail(id)` keys are `[entity, 'detail', id]`.
 *
 * SIL-320 explicit bugfix: `assets/$assetId.tsx` used to invalidate
 * `['client-assets']` after an edit, but the list page
 * (`assets/index.tsx`) actually caches under `['client-assets-all',
 * filterKey]` — the two literals never matched, so an edit never refreshed
 * the list. Fixed here by having both read from `assets.listAll()`.
 */

export const qk = {
  // -- Identity / org --------------------------------------------------
  identity: () => ['client-identity'] as const,
  /** Full `GET /me` (org PUE + audit fields, payment methods, etc.) —
   * deliberately distinct from `identity()`: that key is shared by a
   * narrower `fetchClientIdentity` shape, and mixing queryFns under one
   * react-query key would let whichever fetch lands last silently reshape
   * the other's cached data. */
  me: () => ['client-me'] as const,
  teams: () => ['client-teams'] as const,
  locations: () => ['client-locations'] as const,
  locationsRegions: () => ['client-locations', 'regions'] as const,
  reference: () => ['client-reference'] as const,
  /** Asset classes the org actually OWNS assets in (plus any `includeIds`
   * kept visible so a saved selection is never silently dropped). Distinct
   * key from `reference()` — it is org-scoped holdings, not the taxonomy. */
  ownedAssetClasses: (includeIds: readonly string[] = []) =>
    ['client-reference', 'owned-asset-classes', [...includeIds].sort().join(',')] as const,

  // -- Notifications (SIL-320: was un-prefixed `['notifications', ...]`) --
  notifications: {
    unreadCount: () => ['client-notifications', 'unread-count'] as const,
    list: () => ['client-notifications', 'list'] as const,
    page: (cursor?: string | null) => ['client-notifications', 'page', cursor] as const,
    /** Prefix match — invalidates unreadCount + list + every page. */
    all: ['client-notifications'] as const,
  },

  // -- Payment methods (SIL-320: was un-prefixed `['payment-methods']`) --
  paymentMethods: () => ['client-payment-methods'] as const,

  // -- SKU detail lookup (SIL-320: quotes/new.tsx used un-prefixed
  // `['sku-detail', id]`; opportunities.tsx already used the prefixed form —
  // normalized to the same entity key so both features share one name). --
  skuDetail: (skuId: string | undefined) => ['client-sku-detail', skuId] as const,

  // -- Import review (SIL-1617): one prefix per batch so a decision invalidates all of it --
  importBatch: {
    all: (batchId: string) => ['client-import-batch', batchId] as const,
    review: (batchId: string) => ['client-import-batch', batchId, 'review'] as const,
    proposals: (batchId: string) => ['client-import-batch', batchId, 'proposals'] as const,
    rows: (batchId: string, filter: string) => ['client-import-batch', batchId, 'rows', filter] as const,
    chat: (batchId: string) => ['client-import-batch', batchId, 'chat'] as const,
  },

  // -- Submissions (SIL-320: was un-prefixed `['submission(s)', ...]`) --
  submissions: {
    all: ['client-submissions'] as const,
    detail: (submissionId: string) => ['client-submission', 'detail', submissionId] as const,
  },

  // -- Dashboard ---------------------------------------------------------
  dashboard: {
    summary: () => ['client-dashboard-summary'] as const,
    fmvOutlook: () => ['client-dashboard-fmv-outlook'] as const,
    transactionsPending: () => ['client-transactions-pending'] as const,
    /** SIL-2141 - GET /api/client/dashboard/market-pulse. */
    marketPulse: () => ['client-dashboard-market-pulse'] as const,
  },

  // -- Assets --------------------------------------------------------
  // `listAll` is the one and only "all my assets" list cache — the list
  // page (assets/index.tsx) and the detail page's post-edit invalidation
  // (assets/$assetId.tsx) both key off this (SIL-320 bugfix).
  assets: {
    listAll: (filterKey?: string) =>
      filterKey === undefined
        ? (['client-assets-all'] as const)
        : (['client-assets-all', filterKey] as const),
    detail: (assetId: string) => ['client-asset-detail', assetId] as const,
    /** SIL-1916 - masked unit serials of a stock line, GET /api/client/assets/:assetId/units. */
    units: (assetId: string) => ['client-asset-units', assetId] as const,
    attachments: (assetId: string) => ['client-asset-attachments', assetId] as const,
    provenance: (assetId: string) => ['client-asset-provenance', assetId] as const,
    telemetryScore: (assetId: string) => ['client-asset-telemetry-score', assetId] as const,
    telemetryScoreHistory: (assetId: string) =>
      ['client-asset-telemetry-score-history', assetId] as const,
    /** SIL-347 — customer use-phase (Scope 2, location-based) operational
     * emissions detail, GET /api/client/assets/:assetId/use-phase. */
    usePhase: (assetId: string) => ['client-asset-use-phase', assetId] as const,
    fmvOutlook: (assetId: string) => ['client-asset-fmv-outlook', assetId] as const,
    /** Keyed by the public Fingerprint (passport URL key), not asset.id. */
    passport: (fingerprint: string) => ['client-asset-passport', fingerprint] as const,
    /** Prefix of every `passport(...)` key - invalidates a passport whichever fingerprint keyed it. */
    passportAll: () => ['client-asset-passport'] as const,
    /** SIL-2373 - ledger passport view, GET /api/client/passport/:fingerprint/ledger. */
    passportLedger: (fingerprint: string) => ['client-asset-passport-ledger', fingerprint] as const,
    /** SIL-2409 - owner public-passport state, GET /api/client/passport/:assetId/publication. */
    passportPublication: (assetId: string) => ['client-asset-passport-publication', assetId] as const,
    /** SIL-2409 - QR token metadata, GET /api/client/passport/:assetId/qr-tokens. */
    passportQrTokens: (assetId: string) => ['client-asset-passport-qr-tokens', assetId] as const,
    passportArtifacts: (fingerprint: string) => ['client-asset-passport-artifacts', fingerprint] as const,
    forOpportunity: (conditionBand: string | null | undefined, model: string | null | undefined) =>
      ['client-assets-for-opportunity', conditionBand, model] as const,
    /** SIL-575 — fleet-wide Digital Passports registry, GET /api/client/assets/passports. */
    passportRegistry: () => ['client-assets-passport-registry'] as const,
    /** SIL-585 — Active Verification evidence-panel state, GET
     * /api/client/verification/assets/:assetId/state. */
    verificationState: (assetId: string) => ['client-asset-verification-state', assetId] as const,
    /** Probe asset grouping: a server's accelerator children,
     * GET /api/client/assets/:assetId/components. Shared by the detail card
     * and the list expander so opening one warms the other. */
    components: (assetId: string) => ['client-asset-components', assetId] as const,
    /** Nine drive SMART measurements, GET /api/client/assets/:assetId/drive-smart. */
    driveSmart: (assetId: string) => ['client-asset-drive-smart', assetId] as const,
  },

  // -- Active Verification (SIL-585) ------------------------------------
  verificationRun: (runId: string) => ['client-verification-run', runId] as const,

  // -- Inventory probe (probe asset grouping, Plan 4) --------------------
  inventory: {
    /** Readings made with one freshly minted token, polled by ProbeIngestFeed. */
    ingestsForToken: (tokenId: string) => ['client-inventory-ingests', 'token', tokenId] as const,
  },

  // -- BMC credential store (SIL-585) -----------------------------------
  bmcEndpoints: () => ['client-bmc-endpoints'] as const,

  // -- Lots (Epic 1, lots-first-class) ------------------------------------
  lots: {
    all: ['client-lots'] as const,
    detail: (lotId: string) => ['client-lot', lotId] as const,
  },

  // -- Bench pairing and runs (bench suite walking skeleton) ---------------
  bench: {
    runs: () => ['client-bench', 'runs'] as const,
    run: (runId: string) => ['client-bench', 'runs', 'detail', runId] as const,
    runAnnotations: (runId: string) => ['client-bench', 'runs', 'annotations', runId] as const,
    collectStages: (runId: string) => ['client-bench', 'runs', 'collect-stages', runId] as const,
    pairing: (code: string) => ['client-bench', 'pairing', code] as const,
    // SIL-2246 plans. `planSearch` carries the pairing code (fit is per machine); '' = the plans list, no fit.
    planSearch: (code: string, q: string) => ['client-bench', 'plans', 'search', code, q] as const,
    plansAll: () => ['client-bench', 'plans'] as const,
    plan: (planId: string) => ['client-bench', 'plans', 'detail', planId] as const,
    planPreview: (code: string, planId: string, version: number, deselections: string) =>
      ['client-bench', 'plans', 'preview', code, planId, version, deselections] as const,
    mandatorySet: () => ['client-bench', 'mandatory-set'] as const,
  },

  // -- Partner R2 controls (SIL-2383) -------------------------------------
  r2: {
    all: () => ['client-r2'] as const,
    status: () => ['client-r2', 'status'] as const,
    summary: () => ['client-r2', 'summary'] as const,
    batches: () => ['client-r2', 'batches'] as const,
    candidates: (q: string) => ['client-r2', 'candidates', q] as const,
    units: () => ['client-r2', 'units'] as const,
    draws: () => ['client-r2', 'draws'] as const,
    draw: (id: string) => ['client-r2', 'draws', id] as const,
    dpr: () => ['client-r2', 'dpr'] as const,
    competency: () => ['client-r2', 'competency'] as const,
    team: () => ['client-r2', 'team'] as const,
  },

  // -- Listings ------------------------------------------------------
  listings: {
    all: ['client-listings'] as const,
    detail: (listingId: string) => ['client-listing', listingId] as const,
    skuOptions: () => ['client-listing-sku-options'] as const,
    /** SIL-1349 — `GET /api/client/listings/:id/members/:assetId/passport-summary`,
     * the node Passport drawer's only source. Keyed by BOTH ids: the endpoint
     * re-checks the asset against the listing, so the same asset reached through
     * a different listing is a different authorization decision and must not
     * share a cache entry. */
    nodePassportSummary: (listingId: string, assetId: string) =>
      ['client-listing-node-passport-summary', listingId, assetId] as const,
    /** SIL-1429 — the caller's own interest on this listing, `GET .../interest`. */
    interest: (listingId: string) => ['client-listing-interest', listingId] as const,
    /** SIL-1429 — owner-only anonymized list, `GET .../interests`. */
    interests: (listingId: string) => ['client-listing-interests', listingId] as const,
    /** SIL-1471 — seller-private notes, `GET/POST/PATCH/DELETE .../notes`. */
    notes: (listingId: string) => ['client-listing-notes', listingId] as const,
  },

  // -- Exchange buyer verification (KYB, SIL-1346) ------------------------
  /** `GET /api/client/buyer-verification`. ONE key for the whole app: the
   * verify banner, the status pill, the action card and every action gate
   * read it through `useBuyerStatus()` (hooks/useBuyerStatus.ts), which is
   * the only caller of that endpoint. */
  buyerVerification: () => ['client-buyer-verification'] as const,

  // -- Marketplace / catalog ------------------------------------------
  marketplace: (filters: unknown) => ['client-marketplace', filters] as const,
  catalog: {
    /** Prefix match — invalidates every filter combination. */
    all: ['client-catalog'] as const,
    list: (assetClass: string | undefined, condition: string | undefined, regionId: string | undefined) =>
      ['client-catalog', assetClass, condition, regionId] as const,
  },

  // -- Offers / direct offers ------------------------------------------
  offers: () => ['client-offers'] as const,
  directOffers: () => ['client-direct-offers'] as const,
  // SIL-645: append-only amendment history for one offer.
  offerAmendments: (offerId: string) => ['client-offer-amendments', offerId] as const,

  // -- Opportunities ---------------------------------------------------
  opportunities: {
    all: ['client-opportunities'] as const,
    badge: () => ['client-opportunities-badge'] as const,
  },

  // -- Asset matches (SIL-646) -------------------------------------------
  assetMatches: () => ['client-asset-matches'] as const,

  // -- Transactions ------------------------------------------------------
  transactions: {
    all: ['client-transactions'] as const,
    detail: (transactionId: string) => ['client-transaction', transactionId] as const,
  },

  // -- Custody & Audit Records (SIL-574, I2-D2) ---------------------------
  custodyAuditRecords: {
    all: () => ['client-custody-audit-records'] as const,
  },

  // -- Scenarios ---------------------------------------------------------
  scenarios: {
    all: ['client-scenarios'] as const,
    detail: (scenarioId: string) => ['client-scenario', scenarioId] as const,
    sensitivity: (scenarioId: string | null) => ['client-scenario-sensitivity', scenarioId] as const,
    listingCandidates: (assetClassId: string | null | undefined) =>
      ['client-scenario-listing-candidates', assetClassId] as const,
    // SIL — scenario freeze-on-approval follow-up: GET .../approval-snapshot
    // (migration 748). Its own key, not `detail`'s — the two are invalidated
    // together on approve/unapprove, but the snapshot's `enabled` flag toggles
    // independently of the row fetch (see $scenarioId.tsx).
    approvalSnapshot: (scenarioId: string | null) => ['client-scenario-approval-snapshot', scenarioId] as const,
  },

  // -- Scenario templates (SIL-570, I2-C3) --------------------------------
  scenarioTemplates: {
    all: ['client-scenario-templates'] as const,
  },

  // -- Inventory & Margin (SIL-2535) --------------------------------------
  inventoryMargin: {
    report: (bucket: string | null | undefined) => ['client-inventory-margin', bucket ?? null] as const,
  },

  // -- Financials ---------------------------------------------------------
  financials: {
    assetFinancials: (cursor: string | null | undefined) =>
      ['client-financials-asset-financials', cursor] as const,
    coverage: () => ['client-financials-coverage'] as const,
    depreciation: () => ['client-financials-depreciation'] as const,
    depreciationAssets: (assetClassId: string | null | undefined, cursor: string | null | undefined) =>
      ['client-financials-depreciation-assets', assetClassId, cursor] as const,
    // SIL-573 PR 2 of 2 — Outlook band's market-demand tile.
    marketDemand: () => ['client-financials-market-demand'] as const,
    // Epic 3 (lots-first-class) — dimension is part of the cache key so
    // switching "Group by" never shows a stale class-grouped response.
    opportunity: (dimension: string = 'class') => ['client-financials-opportunity', dimension] as const,
    outlook: () => ['client-financials-outlook'] as const,
    policy: () => ['client-financials-policy'] as const,
    policySuggestions: () => ['client-financials-policy-suggestions'] as const,
    setup: () => ['client-financials-setup'] as const,
    summary: (dimension: string = 'class') => ['client-financials-summary', dimension] as const,
    summaryAssets: (assetClassId: string | null | undefined, cursor: string | null | undefined) =>
      ['client-financials-summary-assets', assetClassId, cursor] as const,
  },

  // -- Market Intelligence (SIL-1071/1072) — org house rates / cost
  // constants + saved purchase analyses --
  marketIntel: {
    costConstants: () => ['client-market-intel-cost-constants'] as const,
    analyses: () => ['client-market-intel-analyses'] as const,
    analysis: (id: string) => ['client-market-intel-analysis', id] as const,
    /** SIL-2543 - POST /api/client/margin-intel/rows, keyed by the committed id set. */
    inlineMargins: (key: string) => ['client-margin-intel-rows', key] as const,
  },

  // -- Sustainability (SIL-439/SIL-442/SIL-441, W4 — carbon targets, org
  // config, baseline trajectory, buy-step reference reads) ----------------
  sustainability: {
    targets: () => ['client-sustainability-targets'] as const,
    config: () => ['client-sustainability-config'] as const,
    trajectory: (horizonMonths: number) => ['client-sustainability-trajectory', horizonMonths] as const,
    gridIntensity: () => ['client-sustainability-grid-intensity'] as const,
    buyCatalog: () => ['client-sustainability-buy-catalog'] as const,
  },

  // -- Environmental (SIL-472, first demo slice) — Overview tab REAL
  // widgets (KPI total/scope2/scope3, footprint treemap, monthly series) --
  environmental: {
    overview: () => ['client-environmental-overview'] as const,
    // SIL-473 — Circularity tab (Sankey repurpose/disposal legs + gpu
    // disposition-comparison cohort). The resale leg and goal bar are
    // sample-only, no query key needed (see lib/circularitySampleData.ts).
    circularity: () => ['client-environmental-circularity'] as const,
    // SIL-477 — Reports tab (verification status slot + available reports).
    reports: () => ['client-environmental-reports'] as const,
    // SIL-476 — Fleet detail tab (class/site breakdowns, MWh overlay, top
    // emitters). KPI strip, monthly S2+S3 bars, FY30 card, and the
    // second-life ledger reuse `overview()`'s cache key unchanged.
    fleetDetail: () => ['client-environmental-fleet-detail'] as const,
  },

  // -- Misc singleton lists ---------------------------------------------
  dataHealth: () => ['client-data-health'] as const,
  marketTrends: () => ['client-market-trends'] as const,
  refreshCandidates: (assetClassId: string | undefined) =>
    ['client-refresh-candidates', assetClassId] as const,
  skuCarbonFootprint: (skuId: string) => ['client-sku-carbon-footprint', skuId] as const,
  modelSuggest: (make: string | undefined) => ['model-suggest', make] as const,
  // SIL-774: step-two of the master/variant picker — a chosen master's
  // direct variants, fetched only once a grain='master' row is picked.
  modelSuggestVariants: (parentSkuId: string | undefined) =>
    ['client-model-suggest-variants', parentSkuId] as const,
  fmvEstimate: (make: string, model: string, condition: string) =>
    ['fmv-estimate', make, model, condition] as const,
};
